'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

interface SystemNoticeProps {
  title: string;
  subtitle?: string;
  badgeLabel?: string;        // e.g. "MURKOFF CORPORATION"
  division?: string;          // e.g. "Behavioral Observation & Compliance Division"
  accessStateLabel?: string;  // e.g. "DENIED", "OFFLINE", "RESTRICTED", "ERROR"
  errorCode?: string;         // e.g. "MK-404-NXRESOURCE"
  children: React.ReactNode;  // body content (instructions, actions, etc.)
}

export function SystemNotice({
  title,
  subtitle,
  badgeLabel = 'MURKOFF CORPORATION',
  division = 'Systems Access Control Division',
  accessStateLabel,
  errorCode,
  children,
}: SystemNoticeProps) {
  return (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 focus:outline-none">
      <div className="max-w-2xl w-full">
        <div
          className={cn(
            'relative overflow-hidden rounded-xl border border-destructive/40',
            'bg-gradient-to-b from-surface via-background to-surface',
            'shadow-[0_0_40px_hsl(var(--destructive)_/_0.35)]'
          )}
        >
          {/* Header bar. The destructive tokens on this screen run at full
              strength on purpose: at /80 and /90 the 12px badge and the 10px
              code readouts drop to ~3.5:1 and ~4.3:1 against the near-black
              --background these pages sit on.

              flex-wrap + min-w-0: at 360px this bar has ~296px to hold a
              0.28em-letterspaced "MURKOFF CORPORATION", a division string up to
              46 characters and "ACCESS STATE: DENIED". Without them both
              columns broke mid-word into ragged blocks; now the access state
              drops to its own line and the division line — flavour text — is
              held back until the xs (400px) step. */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-destructive/50 bg-background/60 px-6 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="h-6 w-6 rounded-sm border border-destructive/80 bg-destructive/70 shrink-0" />
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="text-xs tracking-[0.28em] uppercase text-destructive">
                  {badgeLabel}
                </span>
                <span className="hidden xs:block text-[10px] text-muted-foreground">{division}</span>
              </div>
            </div>
            {accessStateLabel && (
              <span className="shrink-0 text-[10px] font-code text-destructive">
                ACCESS STATE: <span className="font-semibold">{accessStateLabel}</span>
              </span>
            )}
          </div>

          {/* Body */}
          <div className="px-6 py-8 space-y-6">
            <div className="space-y-2">
              {subtitle && (
                <p className="text-[11px] uppercase tracking-[0.35em] text-destructive">
                  {subtitle}
                </p>
              )}
              <h1 className="text-2xl md:text-3xl font-semibold tracking-wide text-foreground">
                {title}
              </h1>
            </div>

            {errorCode && (
              <div className="rounded-lg border border-destructive/60 bg-background/60 px-4 py-3">
                <p className="text-xs font-code text-destructive">
                  ERROR CODE: <span className="font-semibold">{errorCode}</span>
                </p>
              </div>
            )}

            {children}
          </div>

          {/* Scanline overlay */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-soft-light"
            style={{
              backgroundImage:
                'linear-gradient(to bottom, rgba(255,255,255,0.18) 1px, transparent 1px)',
              backgroundSize: '100% 2px',
            }}
          />
        </div>

        {/* Full --muted-foreground, no opacity modifier: 10px letterspaced
            at 0.28em needs the headroom — the zinc-500 this started as landed
            at ~4.1:1 on the page, under the minimum. */}
        <p className="mt-4 text-[10px] text-center text-muted-foreground tracking-[0.28em] uppercase">
          Murkoff Property – Unauthorized Access Prohibited
        </p>
      </div>
    </main>
  );
}
